export class UploadCancelledError extends Error {
  constructor() {
    super('Upload cancelled');
    this.name = 'UploadCancelledError';
  }
}

export interface UploadFileWithProgressOptions {
  file: File;
  signal: AbortSignal;
  uploadUrl: string;
  onProgress: (uploadedBytes: number, totalBytes: number) => void;
}

/**
 * Sends one direct-to-R2 PUT while preserving the browser's byte-accurate transfer events.
 * `fetch` deliberately has no upload-progress API, so file-management surfaces use XHR here.
 */
export function uploadFileWithProgress({
  file,
  signal,
  uploadUrl,
  onProgress,
}: UploadFileWithProgressOptions): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new UploadCancelledError());
      return;
    }

    const request = new XMLHttpRequest();
    const abort = () => request.abort();
    const finish = (callback: () => void) => {
      signal.removeEventListener('abort', abort);
      callback();
    };

    request.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) {
        onProgress(event.loaded, event.total);
      }
    });
    request.addEventListener('load', () => {
      finish(() => {
        if (request.status >= 200 && request.status < 300) {
          onProgress(file.size, file.size);
          resolve();
          return;
        }
        reject(new Error(`Upload failed with status ${request.status}`));
      });
    });
    request.addEventListener('error', () => finish(() => reject(new Error('Upload failed'))));
    request.addEventListener('abort', () => finish(() => reject(new UploadCancelledError())));

    signal.addEventListener('abort', abort, { once: true });
    request.open('PUT', uploadUrl);
    request.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
    request.send(file);
  });
}
